import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import { usePersistentValueLocal } from '../../hooks/usePersistentState';
import { getPlView, updateLine, setLineForecast } from '../../api/financials_pl';
import { putForecastAssumption } from '../../api/forecastAssumptions';
import '../sales/Atterrissage.css';
import './DirectCosts.css';

/* Section « Coûts directs » (Ventes & Ops) — proto validé par Olivier le
   2026-08-15. UNE vue de planification de TOUTES les lignes de charges
   directes (bloc `cogs` du P&L) :
     · chaque ligne porte une RÈGLE (pastille + popover) = la méthode de
       forecast DÉJÀ portée par la ligne côté serveur (aucun moteur dupliqué) ;
     · la règle ÉCRIT une base (génération mono-ligne) — les mois ouverts
       restent modifiables à la main ;
     · le réel comptable s'affiche CONDENSÉ (Σ des mois clos) : on ne ressaisit
       jamais le passé, on ne planifie que les mois ouverts ;
     · les lignes possédées par le circuit stock / plan produits arrivent
       CALCULÉES (badge, lecture seule) ;
     · interrupteur Stock oui/non (hypothèse `direct_costs` par tenant×année) :
       sans stock, la ligne Variation de stock n'a rien à faire à l'écran.
   Remplace au menu « Coût des produits vendus » + « Stock & achats produits »
   (les pages restent routées — retrait définitif à valider avec Olivier). */

const MONTHS = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
const nf = new Intl.NumberFormat('de-CH');
/* Même format que la carte Chiffre d'affaires : entiers, apostrophes suisses,
   CHF ou kCHF selon l'unité choisie en haut de page (2026-08-16). */
const fmtUnit = (chf, unit) => {
  const v = (Number(chf) || 0) / (unit === 'chf' ? 1 : 1000);
  if (Math.abs(v) < 0.5) return '–';
  return nf.format(Math.round(v)).replace(/’|,/g, "'");
};
const cleanNum = (v) => { const x = parseFloat(String(v).replace(/['\s’]/g, '').replace(',', '.')); return Number.isFinite(x) ? x : 0; };

/* Cellule kCHF éditable — bleu sans cadre (charte), stockage CHF. */
function KNum({ chf, onCommit, disabled, unit = 'kchf' }) {
  const [draft, setDraft] = useState(null);
  const [foc, setFoc] = useState(false);
  const k = (Number(chf) || 0) / 1000;
  const shown = draft != null ? draft : foc ? String(k === 0 ? '' : Math.round(k * 1000) / 1000) : fmtUnit(chf, unit);
  if (disabled) return <span className="dc-ro">{fmtUnit(chf, unit)}</span>;
  return (
    <input
      className="dc-num" inputMode="decimal" placeholder="–" value={shown}
      onFocus={() => setFoc(true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setFoc(false); if (draft == null) return; onCommit(cleanNum(draft) * 1000); setDraft(null); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}

/* `embedded` : rendu SANS gabarit de page (titre + segments), pour vivre au
   bas du Plan des ventes — une seule page où l'on voit tout (2026-08-16).
   La vue (mensuel N / N+1 / pluriannuel) est alors imposée par le parent. */
/* `data` (2026-09-12) : une vue au format du compte de résultat fournie par
   l'hôte, à la place de celle de Finance ; Revenus & Marges s'en sert pour se
   lire dans ce même habillage. `title` remplace alors « Coûts directs ». */
/* `cellsEditable` (2026-09-12) : Revenus & Marges règle les RÈGLES ici, mais ses
   mois viennent de son propre calcul et ne se tapent pas ; `onChanged` prévient
   l'hôte qu'une règle a changé, pour qu'il relise sa vue. */
export default function DirectCostsPage({ activeTenant, activeYear, canWrite = true, embedded = false, view: viewProp, unit = 'kchf', refreshKey = 0, onSyncPlan = null, data = null, title = null, cellsEditable = null, onChanged = null }) {
  // Une ligne SANS règle se tape ici, connecté ou non, dans Revenus & Marges
  // comme dans Coûts des ventes (2026-09-12) : Achats, Variation de stock et
  // toute charge directe manuelle. Une ligne sous règle se lit.
  const manualLine = (l) => !l.forecast_method;
  const { t } = useTranslation();
  const tid = activeTenant?.id;
  const year = Number(activeYear) || new Date().getFullYear();
  // Vues comme les plans S&OP : année mensuelle (N, N+1) ou pluriannuel.
  // Largeur de la colonne libellé PARTAGÉE avec le plan de revenus (même clé
  // persistée) : les deux cartes restent alignées même après un glisser de la
  // poignée de redimensionnement (2026-08-16).
  const [nameW] = usePersistentValueLocal(`atterrissage_namew_${tid || 'none'}`, 224);
  const [pvLocal, setPv] = useState('m0');
  const pv = embedded && viewProp ? viewProp : pvLocal;
  const fYear = pv === 'm1' ? year + 1 : year;
  // Mêmes colonnes que la carte Chiffre d'affaires : N-1 réel · N réel ·
  // N prév. · N+1…N+3 plan (2026-08-16).
  const YEARS = Array.from({ length: 5 }, (_, i) => year - 1 + i);
  const PLAN_YEARS = [year + 1, year + 2, year + 3];
  const [annViews, setAnnViews] = useState({});   // pluriannuel : vue par année
  const [viewLocal, setView] = useState(null);
  const view = data || viewLocal;
  const [rule, setRule] = useState(null);          // { lineId, x, y } — popover ouvert
  const [busy, setBusy] = useState(false);
  // Les blocs ne se replient plus : le sous-total se lit sous ses lignes,
  // sans chevron ni pliage (retour Olivier 2026-08-16).
  const wrapRef = useRef(null);

  const load = useCallback(() => {
    if (!tid || data) return;
    getPlView(tid, fYear).then((r) => { if (!r.error) setView(r.data); }).catch(() => {});
  }, [tid, fYear, data]);
  // `refreshKey` : le parent (Plan des ventes) l'incrémente après une
  // régénération — la vue des charges se recharge sans intervention.
  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (!tid || pv !== 'annual') return;
    Promise.all([year - 1, ...YEARS.slice(1), year + 3].map((y) => getPlView(tid, y).then((r) => [y, r.error ? null : r.data]).catch(() => [y, null])))
      .then((pairs) => setAnnViews(Object.fromEntries(pairs)));
    // eslint-disable-next-line
  }, [tid, pv, year]);

  useEffect(() => {
    if (!rule) return undefined;
    // Le menu déroulant d'un <select> est dessiné par le SYSTÈME : cliquer une
    // option produit un mousedown dont la cible est hors du popover, ce qui le
    // refermait au moment même où l'on choisissait la ligne de base
    // (constat 2026-08-16). On ignore donc l'événement tant qu'un champ du
    // popover a le focus.
    const close = (e) => {
      const el = e.target instanceof Element ? e.target : null;
      if (el && el.closest('.dc-pop, .dc-rule')) return;
      const focus = document.activeElement;
      if (focus && focus.closest && focus.closest('.dc-pop')) return;
      setRule(null);
    };
    const esc = (e) => { if (e.key === 'Escape') setRule(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [rule]);

  const closed = Number.isFinite(view?.closed_count) ? view.closed_count : 0;
  // Le plan des ventes est la source (2026-09-27, plus d'interrupteur) : une
  // ligne sous règle se lit, seule une ligne manuelle se tape.
  const cellOk = (l) => canWrite && manualLine(l);
  const openMonths = Array.from({ length: 12 - closed }, (_, i) => closed + 1 + i);   // 1-based
  const fmtU = (chf) => fmtUnit(chf, unit);
  // Mêmes largeurs que le plan de revenus, élargies en CHF.
  const W = unit === 'chf' ? { mo: 78, real: 96, tot: 104, yr: 120 } : { mo: 54, real: 68, tot: 74, yr: 100 };
  const realMonths = Array.from({ length: closed }, (_, i2) => i2 + 1);
  const lines = (view?.lines || []);
  const rowsByCode = {};
  (view?.rows || []).forEach((r) => { if (r.type === 'detail') rowsByCode[r.code] = r; });
  const monthsOf = (id) => rowsByCode[id]?.months || {};
  const sumRange = (id, from, to) => { let s = 0; for (let m = from; m <= to; m++) s += Number(monthsOf(id)[String(m)] || 0); return s; };

  // Même périmètre de verrou que le compte de résultat (lignes possédées par
  // le circuit stock / plan produits → calculées ici aussi).
  const pcpCost = (view?.product_cost_plan && view.product_cost_plan.cost_lines) || [];
  const sopEngaged = !!(view?.stock_plan || pcpCost.length);
  // Une ligne pilotée par le circuit Ventes & Ops reste MODIFIABLE : on ne
  // peut pas rester coincé en production faute d'écran (retour 2026-08-16).
  // La pastille de règle propose « Planifier ici », qui coupe le pilotage.
  const isSop = (l) => pcpCost.includes(l.id)
    || (sopEngaged && (l.line_role === 'purchases' || l.line_role === 'stock_variation'))
    ;

  // Toutes les lignes de charges directes du compte de résultat, telles
  // quelles — la planification du stock ne vit plus ici (décision 2026-08-16).
  const cogsLines = lines.filter((l) => l.section === 'cogs' && !l.hidden);
  /* DEUX BLOCS, comme au compte de résultat (2026-08-16) : les lignes
     valorisées en stock — ou au rôle Achats / Variation de stock — forment
     « Coûts des produits vendus », le reste « Autres coûts directs ». Chaque
     bloc se replie : on lit son total, on déplie pour planifier ligne à ligne. */
  const isGoodsLine = (l) => !!l.stock_linked
    || l.line_role === 'purchases' || l.line_role === 'stock_variation';
  const goodsLines = cogsLines.filter(isGoodsLine);
  const otherLines = cogsLines.filter((l) => !isGoodsLine(l));
  /* `data.plan_goods` (2026-09-12, Revenus & Marges) : le coût des produits
     vendus du plan des ventes fait le TOTAL du bloc sur les mois ouverts ; ses
     lignes se lisent sans projection. */
  /* Revenus & Marges (vue fournie, `data`) : la structure est celle de Finance,
     rien ne s'y reclasse ; Achats y lit le plan des ventes ou se tape (2026-09-14). */
  const rmMode = !!data;
  const planRef = data?.plan_goods || null;   // la consommation du plan, lue dans le popover d'Achats
  const cogsGroups = (goodsLines.length && otherLines.length)
    ? [{ code: 'cogs_goods', label: t('finance:directCosts.gGoods', 'Coûts des produits vendus'), lines: goodsLines },
      { code: 'cogs_other', label: t('finance:directCosts.gOther', 'Autres coûts directs'), lines: otherLines }]
    : [{ code: null, label: null, lines: cogsLines }];
  const groupAt = (grp, m) => grp.lines.reduce((s2, l) => s2 + Number(monthsOf(l.id)[String(m)] || 0), 0);
  const revLines = lines.filter((l) => l.section === 'revenue' && !l.hidden);
  /* Les COMPTES du chiffre d'affaires sont des lignes PLEINES sous leur total
     (dégroupés le 2026-09-01, après un jour de pliage) : on planifie une charge
     directe contre le revenu qui la porte, il n'y a rien à replier. Un filet
     sépare le bloc revenu des charges. */
  const revAt = (m) => revLines.reduce((s, l) => s + Number(monthsOf(l.id)[String(m)] || 0), 0);
  const cogsAt = (m) => cogsLines.reduce((s, l) => s + Number(monthsOf(l.id)[String(m)] || 0), 0);
  const goodsAt = (m) => goodsLines.reduce((s, l) => s + Number(monthsOf(l.id)[String(m)] || 0), 0);
  // Part du chiffre d'affaires net (2026-09-14) : valeur ajoutée et coût des
  // produits vendus, en % du CAN, sous la valeur ajoutée. Une charge est
  // négative : on montre sa part en valeur absolue.
  const pctOf = (num, den) => (Math.abs(den) > 0.5 ? `${(Math.abs(num) / Math.abs(den) * 100).toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %` : '–');
  const sumM = (fn, ms) => ms.reduce((a, m) => a + fn(m), 0);
  const ALL = Array.from({ length: 12 }, (_, i) => i + 1);
  const pctRow = (key, label, numAt) => (
    <tr key={key} className="dc-pct">
      <td className="l">{label}</td>
      <td />
      {realMonths.map((m) => <td key={`r${m}`} className="atl-num">{pctOf(numAt(m), revAt(m))}</td>)}
      {closed > 0 && <td className="atl-filet-l atl-filet-r atl-num">{pctOf(sumM(numAt, realMonths), sumM(revAt, realMonths))}</td>}
      {openMonths.map((m) => <td key={m} className="atl-num">{pctOf(numAt(m), revAt(m))}</td>)}
      {closed > 0 && <td className="atl-filet-l atl-num">{pctOf(sumM(numAt, openMonths), sumM(revAt, openMonths))}</td>}
      <td className="atl-filet-l atl-num">{pctOf(sumM(numAt, ALL), sumM(revAt, ALL))}</td>
      <td />
    </tr>
  );

  const MODE_LABELS = {
    '': t('finance:directCosts.mManual', 'Manuel'),
    last_actual: t('finance:directCosts.mLast', 'Dernier mois'),
    avg_ytd: t('finance:directCosts.mAvg', 'Moyenne YTD'),
    n1_seasonal: t('finance:directCosts.mN1', 'Saisonnalité N-1'),
    target: t('finance:directCosts.mTarget', 'Objectif annuel'),
    pct_of: t('finance:directCosts.mPct', "% d'une ligne"),
    zero: t('finance:directCosts.mZero', 'Vide (0)'),
    sales_purchases: t('finance:directCosts.mSop', 'Ventes & Ops'),
    sales_cogs: t('finance:directCosts.mSop', 'Ventes & Ops'),
    sales_cards: t('finance:directCosts.mSopCards', 'Ventes & Ops — cards'),
    sales_offers: t('finance:directCosts.mOffers', 'Plan des ventes'),   // la règle posée dans Finance, lisible ici (2026-09-14)
  };

  /* Application d'une règle.
     · Les trois paramètres (taux · base · objectif) partent TOUJOURS ensemble :
       le serveur les écrit en bloc (`set_forecast_params`), donc n'en envoyer
       qu'un effaçait les deux autres — le taux effaçait la base et vice-versa
       (constat 2026-08-16, la règle « % d'une ligne » ne s'appliquait jamais).
     · Le popover RESTE ouvert pour les règles qui demandent un paramètre :
       il se fermait à chaque clic, avant même qu'on ait pu saisir le taux.
     · La génération n'a lieu que si les paramètres sont COMPLETS. */
  const applyRule = async (line, patch) => {
    if (!canWrite || busy) return;
    const method = patch.forecast_method !== undefined
      ? patch.forecast_method : (line.forecast_method || null);
    const full = { ...patch, forecast_method: method };
    if (patch.set_forecast_params) {
      full.forecast_rate = patch.forecast_rate !== undefined ? patch.forecast_rate : (line.forecast_rate ?? null);
      full.forecast_base_line_id = patch.forecast_base_line_id !== undefined
        ? patch.forecast_base_line_id : (line.forecast_base_line_id ?? null);
      full.forecast_target = patch.forecast_target !== undefined
        ? patch.forecast_target : (line.forecast_target ?? null);
    }
    const complete = method === 'pct_of'
      ? !!(full.forecast_rate && full.forecast_base_line_id)
      : (method !== 'target' || full.forecast_target != null);
    const needsParams = method === 'pct_of' || method === 'target';
    setBusy(true);
    try {
      await updateLine(tid, line.id, { ...full, year: fYear });   // Manuel fige les mois affichés de cet exercice
      // Le plan des ventes est enregistré AVANT la génération : la ligne de
      // base doit être calculée sur le plan courant, pas sur le précédent.
      if (onSyncPlan) await onSyncPlan();
      // La règle se lit à la relecture (2026-09-12) : rien à générer.
      if (!needsParams || complete) setRule(null);
      load();
      if (onChanged) onChanged();
    } finally { setBusy(false); }
  };
  const editCell = async (line, m, chf) => {
    if (!cellOk(line)) return;
    await setLineForecast(tid, { year: fYear, reporting_line_id: line.id, month: m, amount: chf });
    load();
    if (onChanged) onChanged();
  };

  // ── Vue pluriannuelle : total annuel par ligne, année par année ──────────
  const annRow = (y, id) => {
    const v = annViews[y];
    if (!v) return null;
    const r = (v.rows || []).find((x) => x.type === 'detail' && x.code === id);
    if (!r) return 0;
    let s2 = 0; for (let m = 1; m <= 12; m++) s2 += Number(r.months?.[String(m)] || 0);
    return s2;
  };
  const annRev = (y) => revLines.reduce((s2, l) => s2 + (annRow(y, l.id) || 0), 0);
  const annCogs = (y) => cogsLines.reduce((s2, l) => s2 + (annRow(y, l.id) || 0), 0);

  const togo = (id) => (closed ? sumRange(id, closed + 1, 12) : null);

  /* Colonnes de la vue pluriannuelle, calquées sur la carte Chiffre d'affaires :
     N-1 réel · N réel (mois clos) · N prév. (année entière) · N+1…N+3 plan. */
  const annCols = [
    { key: 'prev', year: year - 1, sub: t('scenarii:atterrissage.subReal', 'réel'), filet: true,
      rev: () => annRev(year - 1), cogs: () => annCogs(year - 1), line: (id) => annRow(year - 1, id) || 0 },
    { key: 'real', year, sub: t('scenarii:atterrissage.realized', 'Réalisé'), filet: true,
      rev: () => realMonths.reduce((a, m) => a + revAt(m), 0),
      cogs: () => realMonths.reduce((a, m) => a + cogsAt(m), 0),
      line: (id) => sumRange(id, 1, closed) },
    { key: 'fore', year, sub: t('scenarii:atterrissage.subForecast', 'prév.'),
      rev: () => Array.from({ length: 12 }, (_, i2) => revAt(i2 + 1)).reduce((a, b) => a + b, 0),
      cogs: () => Array.from({ length: 12 }, (_, i2) => cogsAt(i2 + 1)).reduce((a, b) => a + b, 0),
      line: (id) => sumRange(id, 1, 12) },
    ...PLAN_YEARS.map((y) => ({
      key: `p${y}`, year: y, sub: t('scenarii:atterrissage.subPlan', 'plan'),
      rev: () => annRev(y), cogs: () => annCogs(y), line: (id) => annRow(y, id) || 0,
    })),
  ];

  const popRef = useRef(null);
  useLayoutEffect(() => {
    if (!rule || !popRef.current) return;
    const h = popRef.current.offsetHeight;
    const fits = rule.aBottom + 4 + h <= window.innerHeight - 8;
    const y = fits ? rule.aBottom + 4 : Math.max(8, rule.aBottom - h);
    if (Math.abs(y - rule.y) > 1) setRule((r) => (r ? { ...r, y } : r));
    // eslint-disable-next-line
  }, [rule?.lineId, rule?.aBottom]);

  const ruleLine = rule ? cogsLines.find((l) => l.id === rule.lineId) : null;

  const ruleChip = (l) => {
    const m = l.forecast_method || '';
    /* LA VARIATION DE STOCK NE PORTE JAMAIS DE RÈGLE (2026-09-26) : elle se
       tape, vide vaut zéro, et le stock du bilan roule dessus. La puce le
       disait déjà sous le plan des ventes ; ailleurs elle proposait un choix
       que le serveur refuse désormais. */
    if (l.line_role === 'stock_variation') {
      return <span className="dc-rule dc-rule--fixed" title={t('finance:directCosts.fixedHint', 'Se tape mois par mois ; vide, la variation vaut zéro')}>{MODE_LABELS['']}</span>;
    }
    const param = m === 'pct_of' && l.forecast_rate != null
      ? `${Math.abs(Math.round(l.forecast_rate * 1000) / 10).toLocaleString('de-CH')} %`
      : m === 'target' && l.forecast_target != null
        ? `${fmtU(l.forecast_target)} kCHF`
        : null;
    return (
      <button
        type="button" className="dc-rule" disabled={!canWrite || !rmMode}
        title={t('finance:directCosts.ruleHint', 'Règle de calcul des mois prévisionnels — elle écrit une base, ajustable ensuite mois par mois')}
        onClick={(e) => {
          // Position en coordonnées ÉCRAN (fixed) : le popover se retourne
          // au-dessus de la pastille quand il n'a pas la place en dessous, et
          // ne sort plus par le bas de la fenêtre (constat 2026-08-16).
          const a = e.currentTarget.getBoundingClientRect();
          const left = Math.min(a.left, window.innerWidth - 268);
          // Position VERTICALE calculée après mesure (cf. useLayoutEffect) :
          // sous la pastille si la place existe, sinon BAS du popover aligné
          // sur le bas de la ligne modifiée (demande 2026-08-16).
          setRule({ lineId: l.id, x: Math.round(Math.max(8, left)), y: Math.round(a.bottom + 4),
            aTop: a.top, aBottom: a.bottom });
        }}>
        <span>{MODE_LABELS[m] || m} <i aria-hidden>▾</i></span>
        {param && <em>{param}</em>}
      </button>
    );
  };

  const body = (
      <div className="dc-wrap" ref={wrapRef}>
        <div className="dc-bar">
          {!embedded && (
          <div className="dc-seg">
            {[['m0', t('scenarii:atterrissage.viewMonth', '{{year}} mensuel', { year })],
              ['m1', t('scenarii:atterrissage.viewMonth', '{{year}} mensuel', { year: year + 1 })],
              ['annual', t('scenarii:atterrissage.viewAnnual', 'Plan pluriannuel')]].map(([k, lbl]) => (
              <button key={k} type="button" className={pv === k ? 'on' : ''} onClick={() => setPv(k)}>{lbl}</button>
            ))}
          </div>
          )}
        </div>

        <div className="atl-card dc-card">
          <div className="atl-card__head">
            <h3>{title || t('finance:directCosts.title', 'Coûts directs')}</h3>
            <span className="atl-card__note">{unit === 'chf' ? t('scenarii:atterrissage.inChf', 'en CHF') : t('scenarii:atterrissage.inKchf', 'en milliers de CHF')}</span>
          </div>
          <div className="atl-card__body dc-scroll">
            {pv === 'annual' ? (
            <table className="atl-table atl-table--fixed dc-table--m" style={{ width: nameW + 132 + 2 * W.real + 4 * W.yr + 22 }}>
              <colgroup>
                <col className="atl-col-name" style={{ width: nameW }} />
                <col className="dc-col-rule" />
                {annCols.map((c) => <col key={c.key} style={{ width: c.filet ? W.real : W.yr }} />)}
                <col className="atl-col-del" />
              </colgroup>
              <thead>
                <tr>
                  <th className="l">{t('finance:directCosts.lineCol', 'Ligne de charges')}</th>
                  <th />
                  {annCols.map((c) => (
                    <th key={c.key} className={c.filet ? 'atl-filet-r' : undefined}>
                      {c.year}<br /><span className="atl-sub">{c.sub}</span>
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr className="atl-tot">
                  <td className="l">{t('finance:directCosts.revenue', 'Chiffre d\'affaires net')}</td>
                  <td />
                  {annCols.map((c) => <td key={c.key} className="atl-num">{fmtU(c.rev())}</td>)}
                  <td />
                </tr>
                {revLines.map((l) => (
                  <tr key={`rev-${l.id}`} className="dc-ingroup dc-revline">
                    <td className="l">{l.label}</td>
                    <td />
                    {annCols.map((c) => <td key={c.key} className="atl-num">{fmtU(c.line(l.id))}</td>)}
                    <td />
                  </tr>
                ))}
                <tr className="dc-revgap"><td colSpan={annCols.length + 3} /></tr>
                <tr className="atl-tot">
                  <td className="l">{t('finance:directCosts.header', 'Charges directes')}</td>
                  <td />
                  {annCols.map((c) => <td key={c.key} className="atl-num">{fmtU(c.cogs())}</td>)}
                  <td />
                </tr>
                {cogsLines.map((l) => (
                  <tr key={l.id}>
                    <td className="l">{l.label}</td>
                    <td />
                    {annCols.map((c) => <td key={c.key} className="atl-num">{fmtU(c.line(l.id))}</td>)}
                    <td />
                  </tr>
                ))}
                <tr className="atl-tot dc-sum--va">
                  <td className="l">{t('finance:directCosts.va', 'Valeur ajoutée')}</td>
                  <td />
                  {annCols.map((c) => <td key={c.key} className="atl-num">{fmtU(c.rev() + c.cogs())}</td>)}
                  <td />
                </tr>
                <tr className="dc-gap"><td colSpan={annCols.length + 3} /></tr>
                <tr className="dc-pct">
                  <td className="l">{t('finance:directCosts.vaPct', 'Valeur ajoutée en % du CAN')}</td>
                  <td />
                  {annCols.map((c) => <td key={c.key} className="atl-num">{pctOf(c.rev() + c.cogs(), c.rev())}</td>)}
                  <td />
                </tr>
                <tr className="dc-pct">
                  <td className="l">{t('finance:directCosts.goodsPct', 'Coût des produits vendus en % du CAN')}</td>
                  <td />
                  {annCols.map((c) => <td key={c.key} className="atl-num">{pctOf(goodsLines.reduce((a, l) => a + c.line(l.id), 0), c.rev())}</td>)}
                  <td />
                </tr>
              </tbody>
            </table>
            ) : (
            <table className="atl-table atl-table--fixed dc-table--m" style={{ width: nameW + 132 + 12 * W.mo + (closed > 0 ? W.real + W.tot : 0) + W.tot + 22 }}>
              {/* MÊME colgroup que la carte Chiffre d'affaires : les mois
                  s'alignent d'une carte à l'autre. La colonne RÈGLE est en
                  bout de table pour ne décaler aucun mois (2026-08-16). */}
              <colgroup>
                <col className="atl-col-name" style={{ width: nameW }} />
                <col className="dc-col-rule" />
                {realMonths.map((m) => <col key={`cr${m}`} style={{ width: W.mo }} />)}
                {closed > 0 && <col style={{ width: W.real }} />}
                {openMonths.map((m) => <col key={`cf${m}`} style={{ width: W.mo }} />)}
                {closed > 0 && <col style={{ width: W.tot }} />}
                <col style={{ width: W.tot }} />
                <col className="atl-col-del" />
              </colgroup>
              <thead>
                <tr className="atl-headgroup">
                  <th className="l" />
                  <th />
                  {closed > 0 && <th colSpan={closed} className="atl-grp">{t('scenarii:atterrissage.real', 'réel')}</th>}
                  {closed > 0 && <th className="atl-filet-l atl-filet-r" />}
                  <th colSpan={openMonths.length} className="atl-grp">{t('scenarii:atterrissage.forecast', 'prévisionnel')}</th>
                  {closed > 0 && <th className="atl-filet-l" />}
                  <th className="atl-filet-l" />
                  <th />
                </tr>
                <tr>
                  <th className="l">{t('finance:directCosts.lineCol', 'Ligne de charges')}</th>
                  <th className="dc-th-rule">{t('finance:directCosts.rule', 'Règle')}</th>
                  {realMonths.map((m) => <th key={`r${m}`}>{MONTHS[m - 1]}</th>)}
                  {closed > 0 && (
                    <th className="atl-filet-l atl-filet-r">
                      {t('scenarii:atterrissage.real', 'réel')}<br />
                      <span className="atl-sub">{t('scenarii:atterrissage.nMonths', '{{n}} mois', { n: closed })}</span>
                    </th>
                  )}
                  {openMonths.map((m) => <th key={m}>{MONTHS[m - 1]}</th>)}
                  {closed > 0 && (
                    <th className="atl-filet-l">
                      {t('scenarii:atterrissage.toGo', 'À venir')}<br />
                      <span className="atl-sub">{t('scenarii:atterrissage.nMonths', '{{n}} mois', { n: 12 - closed })}</span>
                    </th>
                  )}
                  <th className="atl-filet-l">{t('finance:directCosts.totalYear', 'Total {{year}}', { year: fYear })}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr className="atl-tot">
                  <td className="l">{t('finance:directCosts.revenue', 'Chiffre d\'affaires net')}</td>
                  <td />
                  {realMonths.map((m) => <td key={`r${m}`} className="atl-num">{fmtU(revAt(m))}</td>)}
                  {closed > 0 && <td className="atl-filet-l atl-filet-r atl-num">{fmtU(realMonths.reduce((a, m) => a + revAt(m), 0))}</td>}
                  {openMonths.map((m) => <td key={m} className="atl-num">{fmtU(revAt(m))}</td>)}
                  {closed > 0 && <td className="atl-filet-l atl-num">{fmtU(openMonths.reduce((a, m) => a + revAt(m), 0))}</td>}
                  <td className="atl-filet-l atl-num">{fmtU(Array.from({ length: 12 }, (_, i2) => revAt(i2 + 1)).reduce((a, b) => a + b, 0))}</td>
                  <td />
                </tr>
                {revLines.map((l) => (
                  <tr key={`rev-${l.id}`} className="dc-ingroup dc-revline">
                    <td className="l">{l.label}</td>
                    <td />
                    {realMonths.map((m) => <td key={`r${m}`} className="atl-num">{fmtU(monthsOf(l.id)[String(m)] || 0)}</td>)}
                    {closed > 0 && <td className="atl-filet-l atl-filet-r atl-num">{fmtU(sumRange(l.id, 1, closed))}</td>}
                    {openMonths.map((m) => <td key={m} className="atl-num">{fmtU(monthsOf(l.id)[String(m)] || 0)}</td>)}
                    {closed > 0 && <td className="atl-filet-l atl-num">{fmtU(togo(l.id))}</td>}
                    <td className="atl-filet-l atl-num">{fmtU(sumRange(l.id, 1, 12))}</td>
                    <td />
                  </tr>
                ))}
                {/* Respiration entre le revenu et ce qu'il coûte. */}
                <tr className="dc-revgap"><td colSpan={realMonths.length + openMonths.length + (closed > 0 ? 2 : 0) + 4} /></tr>
                {/* MÊME STRUCTURE que le compte de résultat (2026-09-12) : le total
                    du bloc en tête, puis ses lignes et leurs sous-totaux. */}
                <tr className="atl-tot">
                  <td className="l">{t('finance:directCosts.header', 'Charges directes')}</td>
                  <td />
                  {realMonths.map((m) => <td key={`r${m}`} className="atl-num">{fmtU(cogsAt(m))}</td>)}
                  {closed > 0 && <td className="atl-filet-l atl-filet-r atl-num">{fmtU(realMonths.reduce((a, m) => a + cogsAt(m), 0))}</td>}
                  {openMonths.map((m) => <td key={m} className="atl-num">{fmtU(cogsAt(m))}</td>)}
                  {closed > 0 && <td className="atl-filet-l atl-num">{fmtU(openMonths.reduce((a, m) => a + cogsAt(m), 0))}</td>}
                  <td className="atl-filet-l atl-num">{fmtU(Array.from({ length: 12 }, (_, i2) => cogsAt(i2 + 1)).reduce((a, b) => a + b, 0))}</td>
                  <td />
                </tr>
                {cogsGroups.map((grp) => (
                  <React.Fragment key={grp.code || 'all'}>
                    {grp.lines.map((l) => (
                      <tr key={l.id} className={grp.code ? 'dc-ingroup' : undefined}>
                        <td className="l">{l.label}</td>
                        <td className="dc-td-rule">{ruleChip(l)}</td>
                        {realMonths.map((m) => <td key={`r${m}`} className="atl-num">{fmtU(monthsOf(l.id)[String(m)] || 0)}</td>)}
                        {closed > 0 && <td className="atl-filet-l atl-filet-r atl-num">{fmtU(sumRange(l.id, 1, closed))}</td>}
                        {openMonths.map((m) => (
                          <td key={m} className="atl-num dc-fore">
                            <KNum
                              unit={unit}
                              chf={monthsOf(l.id)[String(m)] || 0}
                              disabled={!cellOk(l)}
                              onCommit={(chf) => editCell(l, m, chf)}
                            />
                          </td>
                        ))}
                        {closed > 0 && <td className="atl-filet-l atl-num">{fmtU(togo(l.id))}</td>}
                        <td className="atl-filet-l atl-num">{fmtU(sumRange(l.id, 1, 12))}</td>
                        <td />
                      </tr>
                    ))}
                    {grp.code && (
                      <tr className="dc-grp">
                        <td className="l">{grp.label}</td>
                        <td />
                        {realMonths.map((m) => <td key={`r${m}`} className="atl-num">{fmtU(groupAt(grp, m))}</td>)}
                        {closed > 0 && <td className="atl-filet-l atl-filet-r atl-num">{fmtU(realMonths.reduce((a, m) => a + groupAt(grp, m), 0))}</td>}
                        {openMonths.map((m) => <td key={m} className="atl-num">{fmtU(groupAt(grp, m))}</td>)}
                        {closed > 0 && <td className="atl-filet-l atl-num">{fmtU(openMonths.reduce((a, m) => a + groupAt(grp, m), 0))}</td>}
                        <td className="atl-filet-l atl-num">{fmtU(Array.from({ length: 12 }, (_, i2) => groupAt(grp, i2 + 1)).reduce((a, b) => a + b, 0))}</td>
                        <td />
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                <tr className="atl-tot dc-sum--va">
                  <td className="l">{t('finance:directCosts.va', 'Valeur ajoutée')}</td>
                  <td />
                  {realMonths.map((m) => <td key={`r${m}`} className="atl-num">{fmtU(revAt(m) + cogsAt(m))}</td>)}
                  {closed > 0 && <td className="atl-filet-l atl-filet-r atl-num">{fmtU(realMonths.reduce((a, m) => a + revAt(m) + cogsAt(m), 0))}</td>}
                  {openMonths.map((m) => <td key={m} className="atl-num">{fmtU(revAt(m) + cogsAt(m))}</td>)}
                  {closed > 0 && <td className="atl-filet-l atl-num">{fmtU(openMonths.reduce((a, m) => a + revAt(m) + cogsAt(m), 0))}</td>}
                  <td className="atl-filet-l atl-num">{fmtU(Array.from({ length: 12 }, (_, i2) => revAt(i2 + 1) + cogsAt(i2 + 1)).reduce((a, b) => a + b, 0))}</td>
                  <td />
                </tr>
                <tr className="dc-gap"><td colSpan={realMonths.length + openMonths.length + (closed > 0 ? 2 : 0) + 4} /></tr>
                {pctRow('va-pct', t('finance:directCosts.vaPct', 'Valeur ajoutée en % du CAN'), (m) => revAt(m) + cogsAt(m))}
                {pctRow('goods-pct', t('finance:directCosts.goodsPct', 'Coût des produits vendus en % du CAN'), goodsAt)}
              </tbody>
            </table>
            )}
          </div>
        </div>

        {ruleLine && rule && (() => {
          const real = sumRange(ruleLine.id, 1, closed);
          const avg = closed ? real / closed : 0;
          const last = closed ? Number(monthsOf(ruleLine.id)[String(closed)] || 0) : 0;
          const yearTot = sumRange(ruleLine.id, 1, 12);
          const cur = ruleLine.forecast_method || '';
          const unitLbl = unit === 'chf' ? 'CHF' : 'kCHF';
          /* Chaque règle affiche SA valeur sur sa ligne (par mois prévisionnel)
             — plus de bloc d'indicateurs séparé (2026-08-16). */
          const rate = ruleLine.forecast_rate != null
            ? Math.abs(Math.round(ruleLine.forecast_rate * 1000) / 10) : '';
          const sop = isSop(ruleLine);
          // Achats sous le plan des ventes (Revenus & Marges) : deux choix, le
          // plan (la consommation) ou le manuel (2026-09-14).
          const planAvg = planRef ? (() => { const ks = Object.keys(planRef); return ks.length ? ks.reduce((a, k) => a + Number(planRef[k] || 0), 0) / ks.length : 0; })() : null;
          const modes = (rmMode && ruleLine.line_role === 'purchases') ? [
            { m: 'sales_offers', label: t('finance:directCosts.mOffers', 'Plan des ventes'), val: planAvg },
            { m: '', label: t('finance:directCosts.mManual', 'Manuel') },
          ] : [
            { m: '', label: t('finance:directCosts.mManual', 'Manuel') },
            { m: 'last_actual', label: t('finance:directCosts.mLast', 'Dernier mois'), val: last, dis: !closed },
            { m: 'avg_ytd', label: t('finance:directCosts.mAvg', 'Moyenne YTD'), val: avg, dis: !closed },
            { m: 'n1_seasonal', label: t('finance:directCosts.mN1', 'Saisonnalité N-1') },
            { m: 'target', label: t('finance:directCosts.mTarget', 'Objectif annuel') },
            { m: 'pct_of', label: t('finance:directCosts.mPct', "% d'une ligne") },
            { m: 'zero', label: t('finance:directCosts.mZero', 'Vide (0)'), val: 0 },
            // Connexion au module : achats négoce pour la ligne Achats, coût
            // STANDARD des cards (Plan produits / Plan services) pour les
            // autres charges — la variation de stock n'a pas de source ici
            // (flux de bilan). Ajouté le 2026-08-16 : ce choix n'existait que
            // dans Finance, on ne pouvait donc pas brancher depuis cet écran.
            // Dans Revenus & Marges (vue fournie), le lien avec l'ancienne
            // prévision n'est plus proposé : le coût vient du plan des ventes.
            ...(ruleLine.line_role === 'purchases' && !rmMode
              ? [{ m: 'sales_purchases', label: t('finance:directCosts.mSop', 'Ventes & Ops') }] : []),
            // Somme des cards ALLOUÉES à cette ligne (l'allocation se fait
            // card par card, sous le plan de valeur ajoutée).
            ...(ruleLine.line_role !== 'purchases' && ruleLine.line_role !== 'stock_variation' && !rmMode
              ? [{ m: 'sales_cards', label: t('finance:directCosts.mSopCards', 'Ventes & Ops — cards') }] : []),
          ];
          return (
            <div className="dc-pop" ref={popRef} style={{ left: rule.x, top: rule.y }}>
              <div className="dc-pop__h">
                {t('finance:directCosts.ruleTitle', 'Règle de calcul')}
                <span>{fmtU(real)} {unitLbl} {t('finance:directCosts.iRealShort', 'réel')} · {fmtU(yearTot)} {unitLbl} {t('finance:directCosts.iYearShort', 'planifié')}</span>
              </div>
              {/* GROUPE de la ligne (2026-08-16) : « Coûts des produits vendus »
                  ou « Autres coûts directs ». Se réglait seulement en
                  Structure — introuvable depuis l'écran de planification. Les
                  rôles Achats / Variation de stock restent verrouillés. */}
              {/* Le GROUPE d'une ligne appartient à Finance (2026-09-12) : dans
                  Revenus & Marges, on ne reclasse pas, on lit la structure telle quelle. */}
              {!rmMode && (() => {
                const locked = ruleLine.line_role === 'purchases' || ruleLine.line_role === 'stock_variation';
                return (
                  <div className="dc-pop__grp">
                    <span className="dc-pop__grplbl">{t('finance:directCosts.group', 'Groupe')}</span>
                    {[['goods', t('finance:directCosts.gGoods', 'Coûts des produits vendus')],
                      ['other', t('finance:directCosts.gOther', 'Autres coûts directs')]].map(([k, lbl]) => {
                      const on = (k === 'goods') === !!(ruleLine.stock_linked || locked);
                      return (
                        <button key={k} type="button" className={`dc-grpbtn${on ? ' on' : ''}`}
                          disabled={!canWrite || busy || locked || on}
                          onClick={async () => {
                            setBusy(true);
                            try { await updateLine(tid, ruleLine.id, { stock_linked: k === 'goods' }); } finally { setBusy(false); }
                            load();
                            if (onChanged) onChanged();
                          }}>{lbl}</button>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="dc-pop__modes">
                {sop && (
                  <div className="dc-mode dc-mode--free">
                    <button type="button" disabled={!canWrite || busy}
                      title={t('finance:directCosts.freeHint', 'Retire le pilotage par le circuit Ventes & Opérations (cible de stock / coût des ventes) et rend la ligne planifiable ici')}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await Promise.all([
                            putForecastAssumption(tid, fYear, 'stock_plan', null).catch(() => {}),
                            putForecastAssumption(tid, fYear, 'cogs_target', null).catch(() => {}),
                          ]);
                          await applyRule(ruleLine, { forecast_method: null });
                        } finally { setBusy(false); }
                      }}>
                      {t('finance:directCosts.freeLine', 'Planifier ici')}
                    </button>
                    <span className="dc-mode__v">{t('finance:directCosts.sop', 'Ventes & Ops')}</span>
                  </div>
                )}
                {modes.map((o) => (
                  <div key={o.m || 'manual'} className={`dc-mode${cur === o.m ? ' on' : ''}`}>
                    <button
                      type="button" disabled={!canWrite || o.dis || busy}
                      onClick={() => applyRule(ruleLine, { forecast_method: o.m || null })}>
                      {o.label}
                    </button>
                    {/* Paramètre saisi SUR la ligne de sa règle. */}
                    {o.m === 'target' ? (
                      <span className="dc-mode__p">
                        <KNum
                          unit={unit} chf={ruleLine.forecast_target || 0}
                          onCommit={(chf) => applyRule(ruleLine, { forecast_method: 'target', forecast_target: -Math.abs(chf), set_forecast_params: true })}
                        />
                        <em>{unitLbl}</em>
                      </span>
                    ) : o.m === 'pct_of' ? (
                      <span className="dc-mode__p">
                        <select
                          className="dc-sel" value={ruleLine.forecast_base_line_id || ''} disabled={!canWrite}
                          onChange={(e) => applyRule(ruleLine, { forecast_method: 'pct_of', forecast_base_line_id: e.target.value, set_forecast_params: true })}>
                          <option value="">{t('finance:directCosts.pick', 'base…')}</option>
                          {revLines.map((rl) => <option key={rl.id} value={rl.id}>{rl.label}</option>)}
                        </select>
                        <input
                          className="dc-num dc-num--rate" inputMode="decimal" defaultValue={rate}
                          onBlur={(e) => {
                            const v = cleanNum(e.target.value);
                            if (v) applyRule(ruleLine, { forecast_method: 'pct_of', forecast_rate: -Math.abs(v) / 100, set_forecast_params: true });
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        />
                        <em>%</em>
                      </span>
                    ) : o.val != null ? (
                      <span className="dc-mode__v">{fmtU(o.val)} {unitLbl}</span>
                    ) : null}
                  </div>
                ))}
              </div>
              <p className="dc-pop__n">{t('finance:directCosts.ruleNote', 'La règle remplit les mois prévisionnels — ils restent modifiables.')}</p>
            </div>
          );
        })()}
      </div>
  );
  if (embedded) return body;
  return (
    <PageShell timeless className="page-shell--flush" title={t('finance:directCosts.title', 'Coûts directs')}>
      {body}
    </PageShell>
  );
}
